import axios from "axios";
import moment from "moment";
import { baseUrl, customerByPath } from "../../../utils/userInfo";

export async function fetchGraphData(
  { token, solar, battery },
  chartData,
  graphTimeRange,
  customRangeStart,
  customRangeEnd,
  showFinancial,
  rate,
  path,
  fetPriceUSD
) {
  const oneDay = 24 * 3600;
  let now = Math.floor(Date.now() / 1000);
  let from;
  let granularity;
  let labelFormat;

  switch (graphTimeRange) {
    case "Custom":
      from = Math.floor(new Date(customRangeStart).setHours(0, 0, 0, 0) / 1000);
      const end = Math.floor(new Date(customRangeEnd).setHours(0, 0, 0, 0) / 1000);
      const duration = end - from;
      if (duration <= 0) {
        return;
      }
      else if (duration <= oneDay) {
        granularity = "block";
        labelFormat = "DD.MM HH:mm";
      } else if (duration <= 30 * oneDay) {
        granularity = "day";
        labelFormat = "DD MMM";
      } else {
        granularity = "month";
        labelFormat = "MMM YYYY";
      }

      now = end;
      break;
    case "Day":
      from = Math.floor(moment().startOf('day').valueOf() / 1000);
      granularity = "block"; 
      labelFormat = "DD.MM HH:00";
      break;
    case "Week":
      from = moment().subtract(7, "days").unix();
      granularity = "day";
      labelFormat = "DD MMM";
      break;
    case "Month":
      from = moment().subtract(30, "days").unix();
      granularity = "day";
      labelFormat = "DD MMM";
      break;
    case "Year":
    default:
      from = moment().startOf("year").unix();
      granularity = "month";
      labelFormat = "MMM";
      break;
  }

  const agent = customerByPath[path];
  if (!agent) return;

  const labelsSet = new Set();
  const gridMap = {};
  const solarMap = {};
  const batteryMap = {};
  const predictionMap = {};

  // === 1. FETCH GRID / ENERGY HISTORY ===
  const historyResponse = await axios.post(`${agent.agentUrl}/rest/history`, {
    startTimestamp: from,
    endTimestamp: now,
    granularity: granularity,
    type: "",
  });

  const historyData = historyResponse.data.result;

  if (granularity === "block") {
    // agreguj bloky po hodinách
    historyData.forEach((entry) => {
      const ts = new Date(entry.blockStart * 1000);
      ts.setMinutes(0, 0, 0);
      const label = moment(ts).format(labelFormat);
      labelsSet.add(label);

      if (!gridMap[label]) gridMap[label] = 0;

      const energy = entry.energy || 0;
      const price = entry.price || 0;
      const value = showFinancial ? (price / 1e18) * fetPriceUSD : energy;
      gridMap[label] += value;
    });
  } else {
    historyData.forEach((entry) => {
      const raw = entry.day || entry.month;
      const label = moment(raw).format(labelFormat);
      labelsSet.add(label);

      const energy = entry.energy || 0;
      const price = entry.price || 0;
      const value = showFinancial ? (price / 1e18) * fetPriceUSD : energy;
      gridMap[label] = +value.toFixed(2);
    });
  }

  // === 2. FETCH SOLAR ===
  if (solar) {
    const solarResponse = await axios.post(`${baseUrl}/api2-r/energy`, {
      data: "E_I",
      phase: "CUM",
      from,
      to: now,
      period: granularity === "block" ? "HOUR" : granularity.toUpperCase(),
      format: "H",
      token: solar,
    });

    const solarData = solarResponse.data.data.E_I.CUM;

    solarData.forEach(([ts, value]) => {
      const label = moment(ts).format(labelFormat);
      labelsSet.add(label);
      solarMap[label] = showFinancial ? 0 : value;
    });
  }

  // === 3. FETCH BATTERY ===
  if (battery) {
    const batteryResponse = await axios.post(`${baseUrl}/api2-r/energy`, {
      data: "E_I",
      phase: "CUM",
      from,
      to: now,
      period: granularity === "block" ? "HOUR" : granularity.toUpperCase(),
      format: "H",
      token: battery,
    });

    const batteryData = batteryResponse.data.data.E_I.CUM;

    batteryData.forEach(([ts, value]) => {
      const label = moment(ts).format(labelFormat);
      labelsSet.add(label);
      batteryMap[label] = showFinancial ? 0 : value;
    });
  }

  // === 4. FETCH CONSUMPTION PREDICTION ===
  if (graphTimeRange == "Day") {
    const predictionResponse = await axios.post(`${agent.agentUrl}/rest/consumptionPrediction`, {
      text: "",
      session_id: ""
    });

    const predictionData = predictionResponse.data.prediction;

    predictionData.forEach((entry) => {
      const label = moment(entry.timestamp * 1000).format(labelFormat); // Predikcia je od nasledujúcej hodiny
      labelsSet.add(label);
      predictionMap[label] = showFinancial ? 0 : entry.value;
    });
  }

  // === 5. SORT A PRIPRAV LABELS ===
  const sortedLabels = Array.from(labelsSet).sort((a, b) => {
    return moment(a, labelFormat).valueOf() - moment(b, labelFormat).valueOf();
  });

  const gridValues = [];
  const solarValues = [];
  const batteryValues = [];
  const predictionValues = [];
  const totalValues = [];

  sortedLabels.forEach((label) => {
    const grid = +(gridMap[label] ?? NaN).toFixed(2);
    const solar = +(solarMap[label] ?? NaN).toFixed(2);
    const battery = +(batteryMap[label] ?? NaN).toFixed(2);
    const prediction = +(predictionMap[label] ?? NaN).toFixed(2);

    gridValues.push(grid);
    solarValues.push(solar);
    batteryValues.push(battery);
    predictionValues.push(prediction);
    totalValues.push(+(grid + (solar || 0) + (battery || 0)).toFixed(2));
  });

  // === 6. SET CHART DATA ===
  chartData.labels = sortedLabels;
  chartData.datasets = [
    {
      label: showFinancial ? "Grid (€)" : "Grid (kWh)",
      data: gridValues,
      borderColor: "rgb(136, 209, 241)",
      backgroundColor: "rgb(136, 209, 241)",
      fill: false,
      tension: 0.3,
    },
    {
      label: showFinancial ? "Solar (€)" : "Solar (kWh)",
      data: solar ? solarValues : [],
      borderColor: "rgb(244, 208, 63)",
      backgroundColor: "rgb(247, 217, 99)",
      fill: false,
      tension: 0.3,
    },
    {
      label: showFinancial ? "Battery (€)" : "Battery (kWh)",
      data: battery ? batteryValues : [],
      borderColor: "rgb(39, 174, 96)",
      backgroundColor: "rgb(85, 243, 151)",
      fill: false,
      tension: 0.3,
    },
    {
      label: showFinancial ? "Overall consumption (€)" : "Overall consumption (kWh)",
      data: totalValues,
      borderColor: "rgb(209, 3, 3)",
      backgroundColor: "rgba(247, 24, 24, 0.1)",
      borderDash: [2, 2],
      fill: true,
      tension: 0.3,
    },
    ...(graphTimeRange === "Day"
      ? [
          {
            label: showFinancial ? "Predicted (€)" : "Predicted (kWh)",
            data: predictionValues,
            borderColor: "rgba(120, 190, 120, 1)",
            backgroundColor: "rgba(144, 238, 144, 0.1)",
            borderDash: [5, 5],
            fill: false,
            tension: 0.3,
          },
        ]
      : []),
  ];

  return JSON.parse(JSON.stringify(chartData));
}




